// Copyright 2021 Sorama B.V.

import { bindable } from "aurelia-framework";
import { Grid } from "controls/plot/grid-plot/GridData";
import { IPlotParent } from "controls/plot/IPlotParent";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { IExportable } from "libraries/Export/IExportable";
import { SVGNode } from "libraries/SVG/SVGNode";
import { IWorkflowModuleReference } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { Unit } from "units/Unit";
import { Line } from "./LinearAxisPart";

export class DashedLinesPart extends PlotPartBase implements IExportable {
    //#region HTMLElements
    private dashedLinesSvgElement: SVGGElement;
    //#endregion

    //#region Bindables

    @bindable()
    public grid: Grid;

    @bindable()
    public matrixSize: Size;

    @bindable()
    public moduleId: number;
    //#endregion

    //#region Fields

    public dashedLines: Line[] = [];
    public viewBox = "0 0 1 1";

    //#endregion

    //#region Constructor and Initialisation

    constructor(public readonly workflowModuleReference: IWorkflowModuleReference) {
        super(0);
    }

    public bind(context: IPlotParent): void {
        super.bind(context);
    }

    //#endregion

    //#region Change Handlers

    public onMeasureMargin(): Margin {
        return new Margin(0, 0, 0, 0);
    }

    public onSize(size: Size, margin: Margin): void {
        // Wait before setting the size until we get a valid size
        if (size.width - margin.left - margin.right <= 0 || size.height - margin.top - margin.bottom <= 0) return;

        // Check for actual change
        if (
            this.size &&
            this.margin &&
            Math.abs(this.size.width - size.width) < 1e-5 &&
            Math.abs(this.size.height - size.height) < 1e-5 &&
            Math.abs(this.margin.left - margin.left) < 1e-5 &&
            Math.abs(this.margin.top - margin.top) < 1e-5 &&
            Math.abs(this.margin.right - margin.right) < 1e-5 &&
            Math.abs(this.margin.bottom - margin.bottom) < 1e-5
        ) {
            return; // Ignore update
        }

        this.size = size;
        this.margin = margin;
        this.actualSize = new Size(size.width - margin.left - margin.right, size.height - margin.top - margin.bottom);

        this.viewBox = -margin.left + " " + -margin.top + " " + this.size.width + " " + this.size.height;

        this.calculateLines();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>) {
        // Check for actual change
        if (
            this.viewPort &&
            this.viewPortBounds &&
            Math.abs(this.viewPort.x - viewPort.x) < 1e-5 &&
            Math.abs(this.viewPort.y - viewPort.y) < 1e-5 &&
            Math.abs(this.viewPort.width - viewPort.width) < 1e-5 &&
            Math.abs(this.viewPort.height - viewPort.height) < 1e-5 &&
            Math.abs(this.viewPortBounds.x - viewPortBounds.x) < 1e-5 &&
            Math.abs(this.viewPortBounds.y - viewPortBounds.y) < 1e-5 &&
            Math.abs(this.viewPortBounds.width - viewPortBounds.width) < 1e-5 &&
            Math.abs(this.viewPortBounds.height - viewPortBounds.height) < 1e-5
        ) {
            return; // Ignore update
        }

        super.onViewPortComputed(viewPort, viewPortBounds, units);
        this.calculateLines();
    }

    public calculateLines(): void {
        this.dashedLines = [];

        //Total height and width in unit
        const totalX = this.grid.dimension.x * this.grid.spacing.x;
        const totalY = this.grid.dimension.y * this.grid.spacing.y;

        //Careful we're starting at 1 purposefully
        for (let i = 1; i < this.matrixSize.width; i++) {
            //Add vertical lines
            this.dashedLines.push(<Line>{
                x0: this.convertX((totalX / 2) * -1 + (totalX / this.matrixSize.width) * i),
                x1: this.convertX((totalX / 2) * -1 + (totalX / this.matrixSize.width) * i),
                y0: this.convertY((totalY / 2) * -1),
                y1: this.convertY(totalY / 2),
            });
        }

        for (let i = 1; i < this.matrixSize.height; i++) {
            //Add horizontal lines
            this.dashedLines.push(<Line>{
                x0: this.convertX((totalX / 2) * -1),
                x1: this.convertX(totalX / 2),
                y0: this.convertY((totalY / 2) * -1 + (totalY / this.matrixSize.height) * i),
                y1: this.convertY((totalY / 2) * -1 + (totalY / this.matrixSize.height) * i),
            });
        }
    }

    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        if (!this.dashedLinesSvgElement) return undefined;

        const svgs = SVGNode.getSvgsInElement(this.dashedLinesSvgElement);
        if (svgs.length != 1) return undefined; // Only 1 expected..

        return Promise.resolve(svgs[0]);
    }

    //#endregion

    //#region Helpers

    private convertX(x: number): number {
        if (!this.viewPort) {
            return;
        }

        return ((x - this.viewPort.x) / this.viewPort.width) * this.actualSize.width;
    }

    private convertY(y: number): number {
        if (!this.viewPort) {
            return;
        }

        return ((this.viewPort.height - (y - this.viewPort.y)) / this.viewPort.height) * this.actualSize.height;
    }

    //#endregion
}
